// Centralized API utility for handling backend URL configuration
// Check for React-prefixed env var first, then fallback to non-prefixed for backwards compatibility
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

// Global event name dispatched whenever the backend returns a 401 (unauthorized).
// AuthProvider listens to this and clears client auth state, allowing ProtectedRoute
// to redirect to the login page.
export const PCRE_UNAUTHORIZED_EVENT = "pcre:unauthorized";

/**
 * Creates a full API URL by prepending the backend URL
 * @param {string} endpoint - The API endpoint (e.g., "/api/auth/login/")
 * @returns {string} - Full URL with backend domain
 */
export const createApiUrl = (endpoint) => {
  // Remove leading slash if present to avoid double slashes
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  return `${BACKEND_URL}/${cleanEndpoint}`;
};

/**
 * Wrapper for fetch that automatically handles backend URL
 * @param {string} endpoint - The API endpoint
 * @param {Object} options - Fetch options
 * @returns {Promise} - Fetch promise
 */
export const apiFetch = async (endpoint, options = {}) => {
  const fullUrl = createApiUrl(endpoint);
  // Internal option: allow callers to opt out of global 401 handling.
  // Note: strip it so it never reaches the native fetch() options.
  const { suppressUnauthorizedEvent, ...restOptions } = options || {};
  // Always include credentials for session-based authentication
  const fetchOptions = {
    credentials: "include",
    ...restOptions,
  };
  const resp = await fetch(fullUrl, fetchOptions);

  if (!suppressUnauthorizedEvent && resp && resp.status === 401) {
    try {
      if (typeof window !== "undefined" && window?.dispatchEvent) {
        window.dispatchEvent(
          new CustomEvent(PCRE_UNAUTHORIZED_EVENT, {
            detail: {
              endpoint,
              url: fullUrl,
              method: fetchOptions?.method || "GET",
            },
          })
        );
      }
    } catch (_) {
      // Best-effort only; never block the response.
    }
  }

  return resp;
};

/**
 * Get the backend URL for use in components that need it directly
 * @returns {string} - Backend URL
 */
export const getBackendUrl = () => BACKEND_URL;

export default {
  createApiUrl,
  apiFetch,
  getBackendUrl,
};

